import { api } from "@/services/api"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import type { SlotPlanJobStatusValue, SlotPlanScheduleResult } from "@/app/appointments/availability/availabilityApi"

// ── Types ────────────────────────────────────────────────────────────
// Mirror StaffMoveModels.cs (AB#714) as AmsStaffMovesController serializes them. The statuses, roles
// and warning codes are strings on the wire; only the job status is an enum, sent as a number.

/** The body of both calls. */
export interface StaffMoveRequest {
  fromTeamSlug: string
  toTeamSlug: string
  calUserId: number
  /** An instant (ISO 8601); null for now. */
  effectiveAt: string | null
  /** The host's address, checked against `calUserId` by the server. */
  upn?: string | null
}

export type StaffMoveWarningCode = "outreach-overrides" | "part-day-overrides" | "future-plan" | "already-at-to-site"

export interface StaffMoveWarning {
  code: StaffMoveWarningCode | string
  message: string
  /** "YYYY-MM-DD", Pacific. */
  dates: string[]
}

export interface StaffMovePreview {
  fromTeamSlug: string
  toTeamSlug: string
  calUserId: number
  effectiveAt: string
  immediate: boolean
  /** The calendar's booking shape: Booked appointments at the old site ending at or after `effectiveAt`. */
  bookings: BookingDetails[]
  warnings: StaffMoveWarning[]
}

export type StaffMoveSiteRole = "from" | "to"

export type StaffMoveSiteStatus = "added" | "already-member" | "ended" | "ending" | "failed"

export interface StaffMoveSiteResult {
  teamSlug: string
  teamId?: number | null
  role: StaffMoveSiteRole | string
  upn?: string | null
  calUserId: number
  status: StaffMoveSiteStatus | string
  error?: string | null
  activeUntil?: string | null
  jobId?: string | null
  jobStatus?: SlotPlanJobStatusValue | null
  schedules?: SlotPlanScheduleResult[] | null
}

export interface StaffMoveResult {
  fromTeamSlug: string
  toTeamSlug: string
  calUserId: number
  effectiveAt: string
  immediate: boolean
  sites: StaffMoveSiteResult[]
  warnings: StaffMoveWarning[]
}

/** 400 and 404: one sentence per problem. */
export interface StaffMoveProblem {
  errors: string[]
}

// ── Endpoints ────────────────────────────────────────────────────────

// The preview is a POST but reads only, so it is a query: cancelling or moving one of its bookings
// invalidates "AmsBookings", and the list it shows is asked for again. The move changes both sites'
// hosts and the old site's plan, so it drops the plans, the rosters and the calendar.
const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsBookings", "AmsSlotPlans", "AmsTeamMembers"] })

export const staffMovesApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    previewStaffMove: builder.query<StaffMovePreview, StaffMoveRequest>({
      query: (body) => ({ url: "/api/ams/staff-moves/preview", method: "POST", body }),
      providesTags: ["AmsBookings"],
      // Each preview is asked for deliberately and goes stale with the clock; never served from an old one.
      keepUnusedDataFor: 0,
    }),
    moveStaff: builder.mutation<StaffMoveResult, StaffMoveRequest>({
      query: (body) => ({ url: "/api/ams/staff-moves", method: "POST", body }),
      invalidatesTags: ["AmsBookings", "AmsSlotPlans", "AmsTeamMembers"],
    }),
  }),
})

export const { usePreviewStaffMoveQuery, useMoveStaffMutation } = staffMovesApi
